import logger from '@eventbrite/client-logger';
import {
    CheckoutErrorName,
    CheckoutLifecycleEventPostMessageName,
    checkoutPostMessage,
    isWebPlatform,
} from '@eventbrite/checkout-postmessage-api';
import { HAS_WINDOW_LISTENERS } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import has from 'lodash/has';
import { push, replace } from 'react-router-redux';
import { setSubmitFailed, change, submit } from 'redux-form';
import { joinPath } from '@eventbrite/path-utils';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import {
    transformUserInstruments,
    transformVirtualIncentive,
} from '../../../api/transformations/userInstruments';
import {
    getOrganizerQuestionsPage,
    getOrderSuccessAnimationPage,
} from '../../../utils/routes';
import {
    ORDER_TOKEN_HEADER,
    WAITING_ROOM_TOKEN_HEADER,
} from '../../../api/base';
import {
    ERROR_CONTACT_INFO_INCOMPLETE,
    ERROR_EXPRESS_CHECKOUT_ORDER,
} from '../../../constants/expressCheckout';
import { STATSIG_EVENT_NAMES } from '../../../constants/statsig';
import { getIsContactInformationComplete } from '../../../selectors/forms';
import { submitCheckoutForm } from '../../checkout';
import { generateGuestToken } from '../../guest';
import {
    abandonOrder as abandonOrderApi,
    createOrder as createOrderApi,
    getAvailablePaymentMethods as getAvailablePaymentMethodsApi,
    getOrder as getOrderApi,
    getOrderAttendees as getOrderAttendeesApi,
    getOrderEligibility as getOrderEligibilityApi,
    getPaymentHistory as getPaymentHistoryApi,
    resendEmail as resendEmailApi,
    updateEmailPreferences as updateEmailPreferencesApi,
    updateOrder as updateOrderApi,
} from '../../../api/order';
import {
    startApplePaySession,
    initializeApplePay,
    createApplePayPaymentRequestIfExpressCheckout,
} from '../../applePay';
import { getInitSurveysActionFromOrderResponse } from '../../survey';
import {
    addEmailOptInToOrganization as addEmailOptInToOrganizationApi,
    getOrganizer as getOrganizerApi,
} from '../../../api/organization';
import {
    getVaultInstruments as getVaultInstrumentsApi,
    getUserAccountDetails as getUserAccountDetailsApi,
    getCurrentUserAccountDetails,
} from '../../../api/users';

import { getSurvey } from '../../../selectors/survey';
import { logOrderSuccessEvents } from '../../statsig';

import {
    BASE_URL,
    CAPACITY,
    CHECKOUT_FORM_NAME,
    CHECKOUT_PATH,
    DEFAULT_COUNTRY,
    DELIVERY_METHODS_PATH,
    EXTERNAL_PAYMENT_PATH,
    INSTRUMENT_TYPES,
    NOT_FOUND,
    PAYMENT_METHODS,
    PICK_A_SEAT_PATH,
    RESEND_EMAIL_PATH,
    RESEND_EMAIL_FIELD,
    RESEND_EMAIL_FORM_NAME,
    RESERVED_TICKETS_CONFIRMATION_PATH,
    STATUS_PATH,
    TICKETS_FULL_SIZE_PATH,
    TICKETS_WITH_CAPACITY_WAITLIST_PATH,
    WAITING_ROOM_PATH,
    WAITLIST_PATH,
    CHECKOUT_STEP_TIME_OUT,
    LOGIN_PATH,
    TICKETS_PAGE_FORM_NAME,
    PAYMENT_OPTIONS_METHODS_TYPES,
} from '../../../constants';
import {
    InstrumentType,
    PaymentMethodType,
} from '../../../models/paymentMethods/enums';
import { OrderStatus } from '../../../models/order';
import {
    CONFIRM_TO_CHECKOUT_STATUS,
    CONFIRM_CLOSE_STATUS,
    TIME_EXPIRED_STATUS,
    timeExpiredContent,
    STATUS_CONTROL_ORDER_TIME_EXPIRED,
} from '../../../containers/status/constants';
import {
    anyOrderAttendeesTicketClassHasMultipleDeliveryMethods,
    anyOrderAttendeesTicketClassHasShipping,
    getSelectedDeliveryMethodForCountry,
} from '../../../utils/deliveryMethod';
import { refreshEventAndTickets as refreshEventAndTicketsAction } from '../../initialize';
import { getVariantPromoCode } from '../../../utils/promoCodes';
import { ADD_TO_CART, triggerTrackingPixelAction } from '../../pixelAnalytics';
import {
    trackAbandonOrder,
    trackAbandonOrderCancel,
    trackAbandonOrderConfirm,
    trackApplePayShowed,
    trackBackClick,
    trackEmailPreferences,
    trackEmailPreferencesError,
    trackExpressCheckoutClicked,
    trackJoinWaitlistClick,
    trackOrderSubmit,
    trackPaymentMethodSelect,
    trackReopenXPTab,
    trackResendEmailCancel,
    trackResendEmailClick,
    trackResendEmailSubmit,
    trackTicketsPageSubmit,
    trackTimerExpired,
    trackXPBackToCheckout,
} from '../../eventAnalytics';
import { getEventShippingSettings as getEventShippingSettingsAction } from '../../event';
import {
    navigateToTimeExpiredStatusPage,
    startBackgroundTimer as startBackgroundTimerAction,
    updateBackgroundTimer,
} from '../../timer';
import {
    resetWaitingRoomToken as resetWaitingRoomTokenAction,
    updateTokensWithOrderToken as updateTokensWithOrderTokenAction,
    resetOrderToken as resetOrderTokenAction,
} from '../../tokens';
import {
    resetWaitingRoom as resetWaitingRoomAction,
    updateWaitingRoomStep as updateWaitingRoomStepAction,
    updateWaitingRoomWithToken as updateWaitingRoomWithTokenAction,
} from '../../waitingRoom';
import { updateDonationFeeAndTax } from '../../tickets';
import {
    resetOrderAction,
    updateRedirectInstructionsAction,
    updateOrderAttendeesAction,
    updateAvailablePaymentMethodsAction,
    updateOrderEmailAction,
} from '../actions';
import { updateOrderAction } from '..';
import { OrderActionType } from '../types';
import {
    hasDonationTickets,
    hasEnteredDonationAmount,
} from '../../../containers/ticketSelection/utils/ticketUtils';
import {
    getAttendees,
    getIsNosSourceOfTruth,
    getOrderId,
    hasExistingOrder,
} from '../../../selectors/order/typedOrderSelectors';
import {
    isGooglePayAvailableAsPaymentMethod,
    isOrganizerManagingAnAlienOrder,
    isPaidOrder as isOrderToBePaid,
    getIsTransferredOrder,
} from '../../../selectors/order';
import { getSelectedTickets } from '../../../selectors/tickets/getSelectedTickets';
import { getUserId, isUserLoggedIn } from '../../../selectors/user';
import { showPurchaseConfirmation as showPurchaseConfirmationAction } from '../../purchaseConfirmation';
import {
    PAYMENT_CARD_EXPIRATION_DATE,
    PAYMENT_CARD_POSTAL_CODE,
    PAYMENT_CARD_SECURITY_CODE,
    PAYMENT_CREDIT_CARD_NUMBER,
    PAYMENT_SAVE_CREDIT_CARD,
} from '../../../containers/checkout/constants';
import {
    CLEAR_PICK_A_SEAT_BEST_AVAILABLE_WARNING,
    SET_PICK_A_SEAT_BEST_AVAILABLE_WARNING,
} from '../constants';
import { isIdentificationRequiredForCurrency } from '../../../models/currency';
import {
    getUpdateUserInstrumentsAction,
    setVirtualIncentiveAction,
    setUserSiginInMethodsAction,
    clearUserDataAction,
    setUserAsLoggedInAction,
    setLoginFromCheckoutPage,
} from '../../user';
import { retrieveOrderFailureAction } from '../../app';
import {
    setOrganizerMarketingOptInAction,
    setOrganizerNameAndLogoAction,
    setOrganizerUrlAndFollowersAction,
} from '../../organizer';
import { BEST_AVAILABLE_NOT_FOUND_ERROR } from '../../../api/constants';
import {
    getPostMessageActionParamsFromState,
    handleOrderError,
    redirectToExternalPayment,
} from '../utils';
import { getCurrency, getIsFreeEvent } from '../../../selectors/event';
import {
    isSubmitDisabled,
    getCheckoutFormSectionsWithError,
    isFormSubmitted,
} from '../../../selectors/forms';
import { getSelectedPaymentMethod } from '../../../selectors/payment';
import {
    getCanMakePaymentsWithActiveCard,
    isApplePayInCheckoutStepEnabled,
} from '../../../selectors/applePay';
import { getCheckoutFormWithErrorsLabel } from '../../../utils/analytics';
import {
    getIsTicketSelectionCompactSizeFlow,
    getExistingOrderId,
    getPaymentsLaunchStripeElementsFF,
    isEventbriteTLD,
    getIsAppRebrandActive,
} from '../../../selectors/app';
import { TicketsPageSize } from '../../../models/ticketsPage';
import { getPaymentMethodAndNonceFromPaymentData } from '../../../utils/expressCheckout';
import { track } from '@eventbrite/datalayer-library';
import { updateStatsigUserMetadata } from '../../../actions/statsig';
import { initializeGooglePaySDKIfNeededAction } from '../../../actions/googlePay';
import { HeapEventName } from '../../../constants/heap';
import {
    notifyParentOrderCreationError,
    notifyParentOrderPlacingError,
    notifyParentOrderStatusPlaced,
} from '../../notifyParent';
import { getRoutePathname } from '../../../selectors/routing';

export const setPickASeatBestAvailableWarning = (message) => ({
    type: SET_PICK_A_SEAT_BEST_AVAILABLE_WARNING,
    payload: message,
});

export const clearPickASeatBestAvailableWarning = () => ({
    type: CLEAR_PICK_A_SEAT_BEST_AVAILABLE_WARNING,
});

export const updateOrderAttendeesDefaultDeliveryMethodByCountry =
    (selectedCountry) => (dispatch, getState) => {
        const state = getState();
        const attendees = getAttendees(state);
        const attendeesWithDefaultDeliveryMethod = attendees.map(
            ({ deliveryMethod, ticketClass = {}, ...rest }) => ({
                ...rest,
                ticketClass,
                deliveryMethod: getSelectedDeliveryMethodForCountry({
                    deliveryMethods: ticketClass.deliveryMethods,
                    selectedDeliveryMethod: deliveryMethod,
                    selectedCountry,
                }),
            }),
        );

        dispatch(
            updateOrderAttendeesAction(attendeesWithDefaultDeliveryMethod),
        );
    };

const _getOrderFromState = (getState) => getState().order;

export const _handleUpdateOrder = (dispatch, orderResponse) =>
    dispatch(updateOrderAction(orderResponse));

export const _handleUpdateOrderToken = (dispatch, headers) =>
    dispatch(updateTokensWithOrderTokenAction(headers.get(ORDER_TOKEN_HEADER)));

export const setUserSiginInMethods = () => async (dispatch, getState) => {
    const {
        user: { email },
    } = getState();

    if (email) {
        const { response } = await getUserAccountDetailsApi(email);

        if (response && response.sign_in_methods) {
            dispatch(setUserSiginInMethodsAction(response.sign_in_methods));
        }
    }
};

export const getOrganizerData = () => async (dispatch, getState) => {
    const {
        app: { event },
    } = getState();

    if (has(event, 'organizer_id')) {
        const organizerData = await getOrganizerApi(event['organizer_id']);

        dispatch(
            setOrganizerNameAndLogoAction({
                logoUrl: organizerData.response.logo?.url ?? null,
                name: organizerData.response.name,
            }),
        );

        dispatch(
            setOrganizerUrlAndFollowersAction({
                url: organizerData.response.url,
                followedByYou:
                    organizerData.response.follow_status?.followed_by_you ??
                    false,
                numFollowers:
                    organizerData.response.follow_status?.num_followers ?? 0,
            }),
        );

        return dispatch(
            setOrganizerMarketingOptInAction(
                !organizerData.response.disable_marketing_opt_in,
            ),
        );
    }

    return dispatch(setOrganizerMarketingOptInAction(false));
};

export const _initializeOrder = (orderState) => async (dispatch, getState) => {
    const state = getState();
    const { eventId, widgetParentUrl } =
        getPostMessageActionParamsFromState(state);

    await _handleUpdateOrder(dispatch, orderState);

    checkoutPostMessage({
        messageName: CheckoutLifecycleEventPostMessageName.orderStart,
        eventId,
        widgetParentUrl,
    });
};

const _shouldManageUserSavedPaymentData = (state) =>
    isUserLoggedIn(state) && !isOrganizerManagingAnAlienOrder(state);

const _shouldSavePaymentMethods = (state) => {
    // [EB-112961] Currently we can't support vault instruments in currencies requiring identification (like BRL and ARG)
    const isVaultSupportedCurrency =
        !isIdentificationRequiredForCurrency[getCurrency(state)];

    return isVaultSupportedCurrency && _shouldManageUserSavedPaymentData(state);
};

export const getAndSaveAvailablePaymentMethods =
    () => async (dispatch, getState) => {
        const state = getState();
        const {
            user: { publicId: userId } = {},
            tickets: {
                constrainedInstrumentType = '',
                constrainedPaymentMethods = [],
            } = {},
            order: { id: orderId },
            event: { id: eventId },
        } = state;

        const isPaidOrder = isOrderToBePaid(state);

        if (!isPaidOrder) {
            return undefined;
        }

        const { response: availablePaymentData } =
            await getAvailablePaymentMethodsApi(eventId, orderId);

        const stripeFeatureFlag = getPaymentsLaunchStripeElementsFF(state);
        const isFreeEvent = getIsFreeEvent(state);
        if (stripeFeatureFlag && !isFreeEvent) {
            const eventbriteOptions = availablePaymentData.payment_options.find(
                (option) =>
                    option.type === PAYMENT_OPTIONS_METHODS_TYPES.EVENTBRITE,
            );
            // can be undefined for FPP-only events
            if (eventbriteOptions) {
                eventbriteOptions.instrument_types.push({
                    type: PaymentMethodType.STRIPE_CONFIRMATION_TOKEN,
                    variants: [],
                });
            }
        }

        let result = dispatch(
            updateAvailablePaymentMethodsAction(availablePaymentData),
        );

        if (isApplePayInCheckoutStepEnabled(getState())) {
            await dispatch(initializeApplePay());

            dispatch(
                trackApplePayShowed(
                    getCanMakePaymentsWithActiveCard(getState()),
                ),
            );
        }

        if (isGooglePayAvailableAsPaymentMethod(getState())) {
            await dispatch(initializeGooglePaySDKIfNeededAction());
        }

        const virtualIncentiveCardInstrument =
            availablePaymentData.user_instruments?.find(
                (userInstrument) =>
                    userInstrument.instrument?.instrument_type ===
                    InstrumentType.VIRTUAL_INCENTIVES_CARD,
            );

        if (virtualIncentiveCardInstrument) {
            const transformedVirtualIncentiveCardInstrument =
                transformVirtualIncentive(virtualIncentiveCardInstrument);

            if (transformedVirtualIncentiveCardInstrument) {
                dispatch(
                    setVirtualIncentiveAction(
                        transformedVirtualIncentiveCardInstrument,
                    ),
                );
            }
        }

        const hasOnlyOnePaymentMethodAndInstrumentType = (orderState) => {
            return orderState.availablePaymentMethods?.length === 1
                ? orderState.availablePaymentMethods[0].instrumentTypes
                      .length === 1
                : false;
        };

        //TODO EB-243615 Refactor to extract proper selectors

        const shouldSelectPaymentMethod =
            hasOnlyOnePaymentMethodAndInstrumentType(state.order);

        const haveAvailablePaymentMethods = (orderState) =>
            !!orderState.availablePaymentMethods;

        const hasNoPaymentMethodsAvailable = (orderState) =>
            orderState.availablePaymentMethods
                ? orderState.availablePaymentMethods.length === 0
                : true;

        if (
            haveAvailablePaymentMethods(state.order) &&
            hasNoPaymentMethodsAvailable(state.order)
        ) {
            throw new CustomSubmissionError(
                {
                    _error: gettext(
                        'This event is not available for purchase yet. Please contact the organizer in order to complete the purchase.',
                    ),
                },
                {},
            );
        }

        const getFirstAvailableInstrumentType = (orderState) =>
            orderState.availablePaymentMethods
                ? orderState.availablePaymentMethods[0].instrumentTypes[0]
                : null;

        if (shouldSelectPaymentMethod && isPaidOrder) {
            result = dispatch(
                change(
                    CHECKOUT_FORM_NAME,
                    'paymentMethod',
                    getFirstAvailableInstrumentType(state.order).type,
                ),
            );
        }

        // Fetch and save user's stored CC payment methods if user is logged in
        if (_shouldSavePaymentMethods(state)) {
            const { response: instrumentsResponse } =
                await getVaultInstrumentsApi(userId, new Date());

            const transformed = transformUserInstruments(instrumentsResponse);
            const eppMethods = getState().order.availablePaymentMethods.find(
                (method) => method.type === 'eventbrite',
            );

            if (eppMethods) {
                let allowedUserInstruments = transformed.userInstruments;
                const creditCardInstrument = eppMethods.instrumentTypes.find(
                    (instrument) => instrument.type === PAYMENT_METHODS.CREDIT,
                );

                // Filter out user instruments that do not fit in with the allowed CC variants.
                // ex) Don't show stored VISA card if the order can only use AMEX cards.
                if (creditCardInstrument) {
                    const allowedCards = creditCardInstrument.variants;

                    allowedUserInstruments = allowedUserInstruments
                        .filter((userInstrument) =>
                            allowedCards.includes(
                                userInstrument.instrument.cardType,
                            ),
                        )
                        .filter(
                            (userInstrument) =>
                                isEmpty(constrainedPaymentMethods) ||
                                constrainedPaymentMethods.includes(
                                    userInstrument.instrument.cardType,
                                ),
                        );
                }

                dispatch(
                    getUpdateUserInstrumentsAction(allowedUserInstruments),
                );

                if (
                    !isEmpty(allowedUserInstruments) ||
                    constrainedInstrumentType === INSTRUMENT_TYPES.CREDIT_CARD
                ) {
                    /**
                     * If there are stored payment methods or constrained instrument type is 'CREDIT_CARD',
                     * payment method selection is pre-set to CREDIT and the first stored payment method in
                     * the list is pre-selected. If there are no stored payment methods, fall back to 'CREDIT',
                     * which corresponds to the "add a new card" option.
                     */
                    const allowedVaultId = allowedUserInstruments.length
                        ? allowedUserInstruments[0].vaultId
                        : PAYMENT_METHODS.CREDIT;

                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'paymentMethod',
                            PAYMENT_METHODS.CREDIT,
                        ),
                    );
                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.vaultId',
                            allowedVaultId,
                        ),
                    );
                } else {
                    return dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.vaultId',
                            PAYMENT_METHODS.CREDIT,
                        ),
                    );
                }
            }
        }

        return result;
    };

const _checkAndUpdateDonationFeeAndTax =
    (response, selectedTickets) => async (dispatch) => {
        if (!hasDonationTickets(selectedTickets)) {
            return undefined;
        }

        return dispatch(updateDonationFeeAndTax(response));
    };

export const _trackOnCreateOrderError = (error) => {
    let errorType = null;

    try {
        errorType = JSON.parse(error?.extraData?.parsedError)?.error;
    } catch (e) {}

    track({
        eventName: HeapEventName.createOrderError,
        eventData: { type: errorType },
    });
};

export const _handleOnCreateOrderError = (dispatch) => async (error) => {
    _trackOnCreateOrderError(error);

    // first, check if the error is an instance of CustomSubmissionError
    if (error && error.extraData) {
        const { response: { status, headers } = {}, parsedError } =
            error.extraData;

        // check if server respond with a redirect and the WR header
        // and save the info into the store and navigate to WR view
        if (status === 307 && headers.has(WAITING_ROOM_TOKEN_HEADER)) {
            dispatch(
                updateWaitingRoomWithTokenAction(
                    headers.get(WAITING_ROOM_TOKEN_HEADER),
                ),
            );
            dispatch(push(joinPath(BASE_URL, WAITING_ROOM_PATH)));

            return;
        }

        // In case of Existing Order, there is a dim chance that the newly created order is not found
        // This will result in a 404 that will be caught here, we'll then notify the user.
        if (status === 404 && parsedError.error === NOT_FOUND) {
            dispatch(retrieveOrderFailureAction(parsedError));
            throw error;
        }
    }
    await dispatch(
        refreshEventAndTicketsAction(null, error, { skipVarnishCache: true }),
    );
    throw error;
};

const _handleEmailPreferencesError = (error) => {
    if (error.errors) {
        trackEmailPreferencesError(error.errors._error);

        throw error;
    }
};

/**
 * Saves and places an order that is expected to be a redirected payment in NOS workflow (ex. iDEAL, Bancontact, etc...).
 * After placing the order, it will save the redirect instructions returned from the order service and return
 * them at the end of the promise chain.
 *
 * It should be only called within the order actions and no exported outside the folder.
 */
export const getRedirectedPaymentOrderPromiseWithAsyncNosWorkflow =
    (order) => async (dispatch) => {
        try {
            if (!order.redirectionInstructions) {
                // Redirect instructions are required, and if they are not available, that is a serious internal error.
                throw new CustomSubmissionError(
                    {
                        _error: gettext(
                            "There was an error redirecting you to your payment provider's site",
                        ),
                    },
                    {},
                );
            }

            await getPaymentHistoryApi(order.orderId);

            const updateOrderResponse = await updateOrderApi(order.orderId);

            await _handleUpdateOrder(dispatch, updateOrderResponse.response);
            await _handleUpdateOrderToken(
                dispatch,
                updateOrderResponse.headers,
            );

            await dispatch(startBackgroundTimerAction());

            dispatch(
                updateRedirectInstructionsAction(order.redirectionInstructions),
            );
            dispatch(replace(joinPath(BASE_URL, EXTERNAL_PAYMENT_PATH)));

            return order.redirectionInstructions;
        } catch (error) {
            return handleOrderError(dispatch, error);
        }
    };

/**
 * Saves and places an order that is expected to be a redirected payment (ex. iDEAL, Bancontact, etc...).
 * After placing the order, it will save the redirect instructions returned from the order service and return
 * them at the end of the promise chain.
 *
 * It should be only called within the order actions and no exported outside the folder.
 */
export const getRedirectedPaymentOrderPromiseWithLosWorkflow = async ({
    dispatch,
    saveAndPlacePromise,
}) => {
    let redirectInstructions;
    let dispatchPayment;
    let updateOrderRequest;

    try {
        const placeOrderResponse = await saveAndPlacePromise;

        if ('redirect_instructions' in placeOrderResponse.response) {
            redirectInstructions =
                placeOrderResponse.response.redirect_instructions;

            if (redirectInstructions) {
                const orderIdNum = placeOrderResponse.response.id;

                dispatchPayment = () => getPaymentHistoryApi(orderIdNum);
                updateOrderRequest = () => updateOrderApi(orderIdNum);
            }
        } else {
            // Redirect instructions are required, and if they are not available, that is a serious internal error.
            throw new CustomSubmissionError(
                {
                    _error: gettext(
                        "There was an error redirecting you to your payment provider's site",
                    ),
                },
                {},
            );
        }

        await dispatch(dispatchPayment());

        const updateOrderResponse = await updateOrderRequest();

        await _handleUpdateOrder(dispatch, updateOrderResponse.response);
        await _handleUpdateOrderToken(dispatch, updateOrderResponse.headers);

        await dispatch(startBackgroundTimerAction());

        if (redirectInstructions) {
            dispatch(updateRedirectInstructionsAction(redirectInstructions));
            dispatch(replace(joinPath(BASE_URL, EXTERNAL_PAYMENT_PATH)));
        }

        return redirectInstructions;
    } catch (error) {
        return handleOrderError(dispatch, error);
    }
};

export const getOrderSuccessPromise = () => async (dispatch, getState) => {
    const state = getState();
    const {
        preOrderConfirmationSurveySettings: {
            hasQuestions: isOrderFormInTwoPages,
        },
    } = getSurvey(state);
    const isAppRebrandActive = getIsAppRebrandActive(state);

    dispatch(logOrderSuccessEvents());

    if (isAppRebrandActive) {
        dispatch(replace(getOrderSuccessAnimationPage()));
    } else {
        if (isOrderFormInTwoPages) {
            dispatch(replace(getOrganizerQuestionsPage()));
        } else {
            const platform = dispatch(notifyParentOrderStatusPlaced());

            if (isWebPlatform(platform)) {
                dispatch(showPurchaseConfirmationAction());
            }
        }
    }
    await dispatch(resetWaitingRoomAction());
    return dispatch(resetWaitingRoomTokenAction());
};

/**
 * Saves and places an order that will be paid by standard credit card (ex. Visa, Mastercard, etc...).
 * After placing the order, the app will continue to the order confirmation page.
 */

const _getCreditCardOrderPromise = async ({ dispatch, order }) => {
    try {
        await order.saveAndPlacePromise;
        return dispatch(getOrderSuccessPromise());
    } catch (error) {
        return Promise.reject(handleOrderError(dispatch, error));
    }
};

const _tryToRedirectToExternalPayment = (
    paymentMethod,
    order,
    bankId,
    getState,
    dispatch,
) => {
    const { availablePaymentMethods } = _getOrderFromState(getState);

    const isNosSourceOfTruth = getIsNosSourceOfTruth(getState());

    const getRedirectedPaymentOrderPromise = () =>
        isNosSourceOfTruth
            ? dispatch(
                  getRedirectedPaymentOrderPromiseWithAsyncNosWorkflow(order),
              )
            : getRedirectedPaymentOrderPromiseWithLosWorkflow(order);

    //FIXME the argument to new Promise probably shouldn't be an async function
    //eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
        await redirectToExternalPayment(
            paymentMethod,
            () => getRedirectedPaymentOrderPromise(order),
            availablePaymentMethods,
            bankId,
            getState,
            dispatch,
        ).catch((errorMessage) => {
            reject(
                new CustomSubmissionError({
                    _error: errorMessage,
                }),
            );
        });
        HAS_WINDOW_LISTENERS &&
            window.addEventListener('redirectError', (e) => {
                if (e && e.detail) {
                    reject(e.detail);
                }
                resolve();
            });
    });
};

const _requestIdealPayment = ({ order, getState, dispatch }) => {
    if (!order.formData.payment) {
        return new Promise(() => {
            throw new CustomSubmissionError(
                {
                    payment: { bankId: gettext('This field is required') },
                    _error: gettext(
                        'Please correct the highlighted fields below',
                    ),
                },
                {},
            );
        }).catch((error) => handleOrderError(dispatch, error));
    }

    return _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.IDEAL,
        order,
        order.formData.payment.bankId,
        getState,
        dispatch,
    );
};

const _requestNonceBraintreePayment = ({ order, dispatch, getState }) => {
    if (!order.formData.payment) {
        return new Promise(() => {
            throw new CustomSubmissionError(
                {
                    _error: gettext(
                        'You must authorize with PayPal before you can submit your order',
                    ),
                },
                {},
            );
        }).catch((error) => handleOrderError(dispatch, error));
    }
    return _getCreditCardOrderPromise({ order, dispatch, getState });
};

const _requestMaestroBancontactPayment = ({ order, getState, dispatch }) =>
    _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.MAESTRO_BANCONTACT,
        order,
        null,
        getState,
        dispatch,
    );

const _requestSofortPayment = ({ order, getState, dispatch }) =>
    _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.SOFORT,
        order,
        null,
        getState,
        dispatch,
    );

const _requestFPPPaypalPayment = ({ order, getState, dispatch }) =>
    _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.FPP_PAYPAL,
        order,
        null,
        getState,
        dispatch,
    );

const _requestSepaPayment = ({ order, dispatch }) => {
    const formFields = ['ownerName', 'iban', 'ibanAgreement'];
    const firstEmptyField = formFields
        .filter((field) => !get(order.formData.payment, field))
        .shift();

    if (firstEmptyField || !order.formData.payment) {
        return new Promise(() => {
            throw new CustomSubmissionError(
                {
                    payment: {
                        [firstEmptyField]: gettext('This field is required'),
                    },
                    _error: gettext(
                        'Please correct the highlighted fields below',
                    ),
                },
                {},
            );
        }).catch((error) => handleOrderError(dispatch, error));
    }

    return _getCreditCardOrderPromise({ order, dispatch });
};

const _requestStripeConfirmationTokenPayment = ({ order, dispatch }) => {
    const throwException = (errorText) => {
        throw new CustomSubmissionError(
            {
                _error: errorText,
            },
            {
                parsedError: {
                    error: 'RETRY_ERROR',
                    shouldStopSubmit: true,
                },
            },
        );
    };
    const handleError = (errorText) => {
        return new Promise(() => {
            throwException(errorText);
        }).catch((error) => handleOrderError(dispatch, error));
    };

    const callback = order.formData.stripeRedirectedPaymentCallback;
    const paymentIntentClientSecret =
        order.redirectionInstructions?.arguments?.payment_intent_client_secret;

    if (!callback || !paymentIntentClientSecret) {
        return handleError(
            gettext(
                'Cannot process payment, some data is missing. Try another payment method',
            ),
        );
    }

    try {
        return callback(paymentIntentClientSecret, handleError);
    } catch (error) {
        return handleError(error.message);
    }
};

const paymentActions = {
    [PAYMENT_METHODS.IDEAL]: _requestIdealPayment,
    [PAYMENT_METHODS.MAESTRO_BANCONTACT]: _requestMaestroBancontactPayment,
    [PAYMENT_METHODS.NONCE_BRAINTREE]: _requestNonceBraintreePayment,
    [PAYMENT_METHODS.SOFORT]: _requestSofortPayment,
    [PAYMENT_METHODS.SEPA_DIRECT_DEBIT]: _requestSepaPayment,
    [PAYMENT_METHODS.FPP_PAYPAL]: _requestFPPPaypalPayment,
    [PAYMENT_METHODS.STRIPE_CONFIRMATION_TOKEN]:
        _requestStripeConfirmationTokenPayment,
};

export const requestExternalPaymentWithNosWorkflow =
    (paymentMethod, order) => (dispatch, getState) =>
        paymentActions[paymentMethod]({
            order,
            getState,
            dispatch,
        }).catch((error) => handleOrderError(dispatch, error));

export const requestPayment =
    (paymentMethod, order) => (dispatch, getState) => {
        const action =
            paymentActions[paymentMethod] || _getCreditCardOrderPromise;
        return action({
            order,
            getState,
            dispatch,
        }).catch((error) => handleOrderError(dispatch, error));
    };

export const updateEmailPreferencesForOrderOwner =
    (paymentPromise) => (dispatch, getState) =>
        paymentPromise.then(() => {
            const state = getState();
            const {
                app: {
                    featureFlags: { enableEBMarketingOptIn },
                },
                form: {
                    checkoutForm: {
                        values: {
                            ebMarketingOptIn,
                            organizationMarketingOptIn,
                            buyer: {
                                'N-first_name': firstName,
                                'N-last_name': lastName,
                                'N-email': email,
                            },
                        },
                    },
                },
                order: { id: orderId },
                organizer: { enableOrganizerMarketingOptIn },
                event: { organization },
            } = state;

            let ebOptInPromise = Promise.resolve();
            let organizationOptInPromise = Promise.resolve();

            if (ebMarketingOptIn) {
                ebOptInPromise = updateEmailPreferencesApi(
                    orderId,
                    ebMarketingOptIn,
                );
            }

            if (enableOrganizerMarketingOptIn && organizationMarketingOptIn) {
                organizationOptInPromise = addEmailOptInToOrganizationApi(
                    organization.id,
                    email,
                    firstName,
                    lastName,
                );
            }

            // We track the state of both boxes when the user submits their order so we can count how
            // many people actually saw the boxes
            if (enableEBMarketingOptIn) {
                dispatch(
                    trackEmailPreferences(
                        'RF_EB_EMAIL_OPT_IN',
                        ebMarketingOptIn,
                    ),
                );
            }

            if (enableOrganizerMarketingOptIn && get(organization, 'name')) {
                dispatch(
                    trackEmailPreferences(
                        'RF_ORG_EMAIL_OPT_IN',
                        organizationMarketingOptIn,
                    ),
                );
            }

            return Promise.all([
                ebOptInPromise,
                organizationOptInPromise,
            ]).catch((error) => _handleEmailPreferencesError(error));
        });

/**
 * Calls the relevant api to create / retrieve the order
 * In case no order exists (existingOrderId is null/undefined) we are in the standard flow, and we call the usual `createOrderApi` endpoint
 * If the order has previously been created (Add Attendees flow for instance) we just retrieve the order using the new `getOrderApi` endpoint
 *
 * @param {function} dispatch redux dispatch function
 * @param {Object} orderInfo object containing necessary info related to the order
 */
const _callOrderApi = async (dispatch, orderInfo) => {
    const { existingOrderId, eventId } = orderInfo;

    if (existingOrderId) {
        const { response, headers } = await getOrderApi(existingOrderId);

        return {
            orderResponse: {
                order: response,
                attendees: [],
                // Force fetching attendees in order to handle cases with > 50
                forceFetchAttendees: true,
            },
            orderHeaders: headers,
        };
    }

    await generateGuestToken(dispatch, eventId);

    const { response, headers } = await createOrderApi(orderInfo);

    return {
        orderResponse: response,
        orderHeaders: headers,
    };
};

const _processOrder = async (dispatch, getState) => {
    const {
        app: {
            affiliateCode,
            existingOrderId,
            inviteToken,
            campaignToken,
            waitlistCode,
        },
        event: { id: eventId, isReservedSeating },
        groupRegistration: { teamSettings: { token: teamToken } = {} } = {},
    } = getState();

    const state = getState();

    const isEvbTLD = isEventbriteTLD(state);
    const selectedTickets = getSelectedTickets(state);
    const promoCode = getVariantPromoCode(selectedTickets);
    const orderInfo = {
        affiliateCode,
        campaignToken,
        eventId,
        existingOrderId,
        isReservedSeating,
        inviteToken,
        promoCode,
        selectedTickets,
        teamToken,
        waitlistCode,
        isEventbriteTLD: isEvbTLD,
    };

    const { orderResponse, orderHeaders } = await _callOrderApi(
        dispatch,
        orderInfo,
    );

    const attendeesExpansions = ['survey'];

    if (hasExistingOrder(getState())) {
        attendeesExpansions.push('survey_responses');
    }

    if (isReservedSeating) {
        attendeesExpansions.push('ticket_class.reserved_seating');
    } else {
        attendeesExpansions.push('ticket_class');
    }

    const attendeesExpansionsString = attendeesExpansions.join(',');

    // When creating an "empty" order e.g. use Pick-a-seat to select ticket
    // the response returned from order summary is flattened, not having `order` root element.
    const orderData = orderResponse.order || orderResponse;

    // This needs to happen before GET /order/:orderId/attendees fetch, because the request needs token setup.
    await _handleUpdateOrderToken(dispatch, orderHeaders);

    // Create the order first, the response includes `attendees` get paginated and could have a limit to 50.
    // The expected approach is: after order created, paginate over order/:orderId/attendees to get all the attendees
    // This follows the same approach already implemented in organizer app
    const attendees = await getOrderAttendeesApi(
        orderData.id,
        attendeesExpansionsString,
    );
    const orderStatePayload = {
        ...orderData,
        attendees,
    };

    await dispatch(getInitSurveysActionFromOrderResponse(orderStatePayload));
    await dispatch(_initializeOrder(orderStatePayload));

    await dispatch(
        _checkAndUpdateDonationFeeAndTax(orderResponse, selectedTickets),
    );
    await dispatch(getEventShippingSettingsAction());
    await dispatch(
        updateOrderAttendeesDefaultDeliveryMethodByCountry(DEFAULT_COUNTRY),
    );

    // XXX: we are currently dispatching the getAndSaveAvailablePaymentMethods thunk in a couple
    // different places in the checkout flow where GA and Reserved diverged but we should only
    // have to do this at the last step before checkout (ie when the CheckoutPage is rendered).
    if (orderData && !isEmpty(attendees)) {
        await dispatch(getAndSaveAvailablePaymentMethods());
    }

    await dispatch(
        updateWaitingRoomWithTokenAction(
            orderHeaders.get(WAITING_ROOM_TOKEN_HEADER),
        ),
    );

    dispatch({
        type: OrderActionType.updateOrder,
        payload: {
            isPlaced: false,
        },
    });

    track({
        eventName: HeapEventName.createOrderSuccess,
        eventData: { orderId: orderData.id },
    });

    dispatch(updateStatsigUserMetadata);

    return dispatch(startBackgroundTimerAction());
};

const _updateIfNewUserLoggedIn = async (dispatch, getState, userInfo) => {
    // There's a logged in user, check if it's the same than in the app state
    const state = getState();
    const userId = getUserId(state);

    if (userId !== userInfo.id) {
        // Not the same user, clear previous user and set the new one
        await dispatch(clearUserDataAction());
        return dispatch(setUserAsLoggedInAction(userInfo));
    }
};

export const updateLoggedInUserIfChanged = async (dispatch, getState) => {
    // Check against the API if the user in the App is still valid.
    // If not, update the state.
    try {
        const { response: userInfoResponse } =
            await getCurrentUserAccountDetails();

        return _updateIfNewUserLoggedIn(dispatch, getState, userInfoResponse);
    } catch (e) {
        // Check if the user is logged out. If so, only update if the app initialized with a user
        const parsedError = e?.extraData?.parsedError;
        const state = getState();

        if (parsedError?.error === 'NOT_AUTHORIZED' && isUserLoggedIn(state)) {
            dispatch(clearUserDataAction(state));
        }
    }
};

export const createPickASeatOrder = () => async (dispatch, getState) => {
    try {
        await _processOrder(dispatch, getState);
        return dispatch(push(joinPath(BASE_URL, PICK_A_SEAT_PATH)));
    } catch (customSubmissionError) {
        /**
         * When "ERROR_NO_MATCH_FOUND" inventory error raised after clicking "Buy on Map"
         * Clear the selected tickets and go to pick a seat with emtpy order.
         * Set warning state so that an warning notification would be displayed in pick a seat view.
         */
        if (
            get(customSubmissionError, 'errors._error') ===
            BEST_AVAILABLE_NOT_FOUND_ERROR
        ) {
            await dispatch(
                refreshEventAndTicketsAction(null, customSubmissionError),
            );
            await dispatch(
                setPickASeatBestAvailableWarning(
                    BEST_AVAILABLE_NOT_FOUND_ERROR,
                ),
            );

            return dispatch(createPickASeatOrder());
        }

        return _handleOnCreateOrderError(dispatch)(customSubmissionError);
    }
};

const shouldShowDeliveryMethodPage = (attendees) => {
    const hasMultipleDeliveryMethods =
        anyOrderAttendeesTicketClassHasMultipleDeliveryMethods(attendees);
    const hasShippingDeliveryMethod =
        anyOrderAttendeesTicketClassHasShipping(attendees);

    return hasMultipleDeliveryMethods || hasShippingDeliveryMethod;
};

const getPathAfterReservedSeating = (state) => {
    const attendees = getAttendees(state);

    return shouldShowDeliveryMethodPage(attendees)
        ? DELIVERY_METHODS_PATH
        : CHECKOUT_PATH;
};

export const goToPageAfterReservedSeating = () => (dispatch, getState) => {
    const path = getPathAfterReservedSeating(getState());

    return dispatch(push(joinPath(BASE_URL, path)));
};

export const getPathAfterTicketSelection = ({
    shouldShowReservedTicketsFlow,
    attendees,
}) => {
    let path = CHECKOUT_PATH;

    if (shouldShowReservedTicketsFlow) {
        path = RESERVED_TICKETS_CONFIRMATION_PATH;
    } else if (shouldShowDeliveryMethodPage(attendees)) {
        path = DELIVERY_METHODS_PATH;
    }

    return joinPath(BASE_URL, path);
};

export const createOrder =
    ({ shouldRedirect }) =>
    async (dispatch, getState) => {
        try {
            await _processOrder(dispatch, getState);

            if (!shouldRedirect) {
                return;
            }

            const state = getState();
            const {
                event: { isReservedSeating },
            } = state;
            const shouldShowReservedTicketsFlow =
                isReservedSeating && !getIsTransferredOrder(state);
            const attendees = getAttendees(state);

            return dispatch(
                push(
                    getPathAfterTicketSelection({
                        shouldShowReservedTicketsFlow,
                        attendees,
                    }),
                ),
            );
        } catch (error) {
            let errorCode;
            try {
                errorCode = JSON.parse(error?.extraData?.parsedError)?.error;
            } catch {
                errorCode = CheckoutErrorName.GENERIC_ERROR;
            }

            dispatch(notifyParentOrderCreationError(errorCode));

            return _handleOnCreateOrderError(dispatch)(error);
        }
    };

const statsigAbandonOrderEventValue = (getState) => {
    const state = getState();
    return getIsTicketSelectionCompactSizeFlow(state)
        ? TicketsPageSize.compact
        : TicketsPageSize.full;
};

/**
 * Try to abandon the order, and if something went wrong we will ignore the error
 * Order will be abandoned anyway when its TTL gets to 0 inside Redis
 */
export const abandonOrder = () => async (dispatch, getState) => {
    const state = getState();
    const orderId = getOrderId(state);

    if (!orderId) {
        return undefined;
    }

    try {
        dispatch(setLoginFromCheckoutPage(false));
        await abandonOrderApi(orderId);
        dispatch({
            type: OrderActionType.updateOrder,
            payload: {
                isPlaced: false,
            },
        });
        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_ALL_SUCCESS_ABANDON_ORDER,
            statsigAbandonOrderEventValue(getState),
        );
        return dispatch(trackAbandonOrderConfirm());
    } catch (e) {
        return undefined;
    }
};

/**
 * The user abandons the order by closing the browser tab or window before paying
 */
export const abandonOrderOnClose = () => (dispatch, getState) => {
    const {
        order: { id: orderId, status: orderStatus },
    } = getState();

    if (
        orderId &&
        (orderStatus === OrderStatus.started ||
            orderStatus === OrderStatus.pending)
    ) {
        abandonOrderApi(orderId);
        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_ALL_SUCCESS_ABANDON_ORDER,
            statsigAbandonOrderEventValue(getState),
        );
        return dispatch(trackAbandonOrderConfirm());
    }
};

/**
 * The order is not abandoned and the user remains in the checkout flow.
 * User will be taken back to a page of the given path, default is the checkout page.
 * A tracking action is also triggered to save the user action.
 */
export const backToPathOnCancelAbandonOrder =
    (pathName = CHECKOUT_PATH) =>
    (dispatch) => {
        dispatch(trackAbandonOrderCancel());

        return dispatch(push(joinPath(BASE_URL, pathName)));
    };

export const backToLogin = () => (dispatch) => {
    dispatch(replace(joinPath(BASE_URL, LOGIN_PATH)));
};

/**
 * The user has attempted to abandon the order by clicking the back button. This sends the user to a page
 * with a confirmation dialog and triggers a tracking action to save the user action.
 */
export const abandonOrderBackAttemptWithStatusPath =
    (
        pathName = CHECKOUT_PATH,
        statusPageSubPath = CONFIRM_TO_CHECKOUT_STATUS,
    ) =>
    (dispatch) => {
        dispatch(trackBackClick(pathName));
        return dispatch(
            push(joinPath(BASE_URL, STATUS_PATH, statusPageSubPath)),
        );
    };

export const goToPageBeforeCheckout = () => (dispatch, getState) => {
    const state = getState();
    const attendees = getAttendees(state);

    track({
        eventName: HeapEventName.previousPageClick,
        eventData: {
            pathname: getRoutePathname(getState()),
        },
    });

    if (shouldShowDeliveryMethodPage(attendees)) {
        dispatch(trackBackClick(CHECKOUT_PATH));
        return dispatch(push(joinPath(BASE_URL, DELIVERY_METHODS_PATH)));
    }

    return dispatch(abandonOrderBackAttemptWithStatusPath(CHECKOUT_PATH));
};

/**
 * The user has attempted to abandon the order by clicking the modal close button. This sends the user to a page
 * with a confirmation dialog and triggers a tracking action to save the user action.
 */
export const abandonOrderCloseAttemptWithStatusPath =
    ({ pathName, statusPath = CONFIRM_CLOSE_STATUS, fullPathName }) =>
    (dispatch) => {
        track({
            eventName: HeapEventName.modalCloseButtonClick,
            eventData: {
                pathname: fullPathName,
            },
        });
        dispatch(trackAbandonOrder(pathName));
        return dispatch(push(joinPath(BASE_URL, STATUS_PATH, statusPath)));
    };

/**
 * The user has attempted to back out of an external payment by clicking the back button. This sends the user
 * back to the checkout form page with all the fields filled out how they were before.
 */
export const backToCheckoutFromExternalPayment = () => (dispatch) => {
    dispatch(trackXPBackToCheckout());
    dispatch(setSubmitFailed(CHECKOUT_FORM_NAME));

    return dispatch(replace(joinPath(BASE_URL, CHECKOUT_PATH)));
};

const _isExternalPaymentPageActiveWindow = () => document.hidden;

/**
 * The timer expired. This sends the user back to the checkout and show the timer expired message.
 */
export const onTimerExpiredFromExternalPayment = () => (dispatch, getState) => {
    const {
        order: { status: orderStatus },
    } = getState();

    if (!STATUS_CONTROL_ORDER_TIME_EXPIRED.includes(orderStatus)) {
        dispatch(trackTimerExpired(EXTERNAL_PAYMENT_PATH));
        track({
            eventName: HeapEventName.timerExpired,
            eventData: {
                checkoutStep: EXTERNAL_PAYMENT_PATH,
            },
        });
        dispatch(replace(joinPath(BASE_URL, STATUS_PATH, TIME_EXPIRED_STATUS)));
        if (_isExternalPaymentPageActiveWindow()) {
            alert(timeExpiredContent({}).alertText); // eslint-disable-line no-alert
        }
    }
};

/**
 * The user has attempted to reopen the tab to their bank's website from the external payment page. This will
 * open a new tab to the same URL used before.
 */
export const reopenExternalPaymentTab = () => (dispatch, getState) => {
    const state = getState();
    const {
        checkoutForm: {
            values: { paymentMethod },
        },
    } = state.form;

    const { availablePaymentMethods } = _getOrderFromState(getState);
    const redirectInstructions =
        _getOrderFromState(getState).redirectInstructions;
    const userTriggered = true;

    let bankId;

    if (paymentMethod === PAYMENT_METHODS.IDEAL) {
        const {
            checkoutForm: {
                values: { payment },
            },
        } = state.form;

        bankId = payment.bankId;
    }

    dispatch(trackReopenXPTab());

    redirectToExternalPayment(
        paymentMethod,
        async () => redirectInstructions,
        availablePaymentMethods,
        bankId,
        getState,
        userTriggered,
    );
};

/**
 * Prefilling email input to resend the confirmation email with the email used in the order
 */
export const prefillResendEmailInput = () => (dispatch, getState) => {
    const {
        order: { email: orderEmail },
    } = getState();

    dispatch(
        change(RESEND_EMAIL_FORM_NAME, RESEND_EMAIL_FIELD, orderEmail, true),
    );
};

/**
 * There is an attempt to resend the order to another email, a form to enter the new email is shown.
 * A tracking action is triggered to save the user action.
 */
export const resendEmailAttempt = () => (dispatch) => {
    dispatch(trackResendEmailClick());

    return dispatch(push(joinPath(BASE_URL, RESEND_EMAIL_PATH)));
};

/**
 * The order is sent to the new email.
 * A tracking action is triggered to save the user action.
 */
export const resendEmail =
    ({ resendEmail: email }) =>
    async (dispatch, getState) => {
        const state = getState();
        const orderId = getOrderId(state);

        await resendEmailApi(orderId, email);
        await dispatch(updateOrderEmailAction({ email }));

        await dispatch(showPurchaseConfirmationAction());

        return dispatch(trackResendEmailSubmit());
    };

/**
 * The order is not sent to the new email and the user goes back to the status page.
 * A tracking action is triggered to save the user action.
 */
export const cancelResendEmail = () => (dispatch) => {
    dispatch(trackResendEmailCancel());

    return dispatch(showPurchaseConfirmationAction());
};

/**
 * This is triggered when clicking "Checkout" from reserved event "Buy on Map" flow
 * It will make sure the selected tickets have correct delivery methods,
 * such as use backup method if shipping cut-off date has passed, and select the correct
 * default delivery method.
 */
export const checkoutFromBuyOnMap = () => async (dispatch, getState) => {
    const state = getState();
    const { order } = state;

    try {
        const { response: eligibility } = await getOrderEligibilityApi(
            order.id,
        );
        const orderWithEligibility = {
            ...order,
            eligibility,
        };

        dispatch(updateOrderAction(orderWithEligibility));

        if (eligibility.eligible) {
            // XXX: we are currently dispatching the getAndSaveAvailablePaymentMethods thunk in a couple
            // different places in the checkout flow where GA and Reserved diverged but we should only
            // have to do this at the last step before checkout (ie when the CheckoutPage is rendered).
            await dispatch(getAndSaveAvailablePaymentMethods());
            await dispatch(getEventShippingSettingsAction());
            await dispatch(
                updateOrderAttendeesDefaultDeliveryMethodByCountry(
                    DEFAULT_COUNTRY,
                ),
            );
            await dispatch(goToPageAfterReservedSeating());
        }
    } catch (e) {
        throw e;
    }
};

/**
 * Dispatch the correct actions on clicking Checkout from the Ticket Selection Page
 */
export const submitFromTicketSelectionPage =
    () => async (dispatch, getState) => {
        dispatch(triggerTrackingPixelAction(ADD_TO_CART));
        dispatch(trackTicketsPageSubmit(TICKETS_FULL_SIZE_PATH));
        await updateLoggedInUserIfChanged(dispatch, getState);

        const order = await dispatch(
            createOrder({
                shouldRedirect: true,
            }),
        );

        return order;
    };

/**
 * Dispatch the correct actions when clicking Express Checkout (ApplePay button) from the Ticket Selection Page
 */
export const startExpressCheckoutApplePaySession = () => async (dispatch) => {
    dispatch(triggerTrackingPixelAction(ADD_TO_CART));
    dispatch(trackTicketsPageSubmit(TICKETS_FULL_SIZE_PATH));
    await dispatch(createApplePayPaymentRequestIfExpressCheckout());
    dispatch(startApplePaySession(true));
};

/**
 * Dispatch the correct actions when the ApplePay payment is authorised, so that create and place order requests happen
 */
export const createAndPlaceOrderExpressCheckout =
    (payment) => async (dispatch, getState) => {
        const { nonce, paymentMethod } =
            getPaymentMethodAndNonceFromPaymentData(payment);

        logEvent(STATSIG_EVENT_NAMES.CHECKOUT_STEP_PLACE_ORDER_CLICK);
        dispatch(trackExpressCheckoutClicked(TICKETS_FULL_SIZE_PATH));

        dispatch(change(TICKETS_PAGE_FORM_NAME, nonce, null));

        if (!getIsContactInformationComplete(getState())) {
            dispatch(
                notifyParentOrderPlacingError(
                    CheckoutErrorName.ERROR_CONTACT_INFO_INCOMPLETE,
                ),
            );

            throw new CustomSubmissionError(
                {
                    _error: ERROR_CONTACT_INFO_INCOMPLETE,
                },
                {},
            );
        }

        try {
            await dispatch(
                createOrder({
                    shouldRedirect: false,
                }),
            );

            dispatch(
                change(CHECKOUT_FORM_NAME, 'paymentMethod', paymentMethod),
            );

            dispatch(trackPaymentMethodSelect(paymentMethod));

            dispatch(trackOrderSubmit(paymentMethod, false));

            await dispatch(submitCheckoutForm({}));
        } catch (e) {
            logger.error(e);

            dispatch(resetOrderAction());
            dispatch(resetOrderTokenAction());

            dispatch(
                notifyParentOrderPlacingError(
                    CheckoutErrorName.ERROR_EXPRESS_CHECKOUT_ORDER,
                ),
            );

            throw new CustomSubmissionError(
                {
                    _error: ERROR_EXPRESS_CHECKOUT_ORDER,
                },
                {},
            );
        }
    };

/**
 * This handles what onSubmit should do for events that have their event capacity waitlist triggered:
 * - Happy path: There are no unlimited donation tickets: clicking submit (Join Waitlist) should go to the regular Waitlist flow.
 * - With an unlimited donation ticket:
 *      - No donation amount entered: clicking submit (Join Waitlist) should go to the Waitlist flow.
 *      - When a donation amount is entered, clicking submit (Checkout) should dispatch the regular order actions to start an order.
 */
export const submitFromCapacityWaitlistTicketsPage =
    () => (dispatch, getState) => {
        const {
            tickets: { ticketsById },
        } = getState();

        if (hasEnteredDonationAmount(ticketsById)) {
            dispatch(submitFromTicketSelectionPage());
        } else {
            dispatch(
                trackJoinWaitlistClick(
                    TICKETS_WITH_CAPACITY_WAITLIST_PATH,
                    CAPACITY,
                ),
            );
            dispatch(push(joinPath(BASE_URL, WAITLIST_PATH)));
        }
    };

export const clearPaymentFields = () => (dispatch) => {
    dispatch(change(CHECKOUT_FORM_NAME, PAYMENT_CREDIT_CARD_NUMBER, ''));
    dispatch(change(CHECKOUT_FORM_NAME, PAYMENT_CARD_EXPIRATION_DATE, ''));
    dispatch(change(CHECKOUT_FORM_NAME, PAYMENT_CARD_SECURITY_CODE, ''));
    dispatch(change(CHECKOUT_FORM_NAME, PAYMENT_CARD_POSTAL_CODE, ''));
    dispatch(change(CHECKOUT_FORM_NAME, PAYMENT_SAVE_CREDIT_CARD, false));
};

export const onTimerExpired = () => (dispatch, getState) => {
    const isSubmitted = isFormSubmitted(getState(), CHECKOUT_FORM_NAME);

    dispatch(updateBackgroundTimer());

    if (isSubmitted) {
        return;
    }

    dispatch(trackTimerExpired(CHECKOUT_PATH));
    track({
        eventName: HeapEventName.timerExpired,
        eventData: {
            checkoutStep: CHECKOUT_PATH,
        },
    });
    dispatch(updateWaitingRoomStepAction(CHECKOUT_STEP_TIME_OUT));
    dispatch(navigateToTimeExpiredStatusPage());
};

export const handleCheckoutRouting = () => (dispatch, getState) => {
    const selectedPaymentMethod = getSelectedPaymentMethod(getState());
    const formErrors = getCheckoutFormSectionsWithError(
        getState(),
        CHECKOUT_FORM_NAME,
    );
    const trackOrderSubmitLabel =
        getCheckoutFormWithErrorsLabel(formErrors) || selectedPaymentMethod;

    dispatch(trackOrderSubmit(trackOrderSubmitLabel, !!formErrors.length));

    const isCheckoutFormEnabled = !isSubmitDisabled(
        getState(),
        CHECKOUT_FORM_NAME,
    );

    if (
        selectedPaymentMethod === PaymentMethodType.APPLE_PAY &&
        isCheckoutFormEnabled // When there are errors we have to submit the form so Redux-form highlights them
    ) {
        return dispatch(startApplePaySession());
    }

    return dispatch(submit(CHECKOUT_FORM_NAME));
};

export const getExistingOrderData = () => async (dispatch, getState) => {
    const existingOrderId = getExistingOrderId(getState());

    if (existingOrderId) {
        try {
            const { response } = await getOrderApi(existingOrderId);
            await dispatch(updateOrderAction(response));
            await dispatch(getInitSurveysActionFromOrderResponse(response));
        } catch (customSubmissionError) {
            // TODO: EB-229798 - Error handling. Communicate with my order to display there the error message (postMessage)
        }
    }
};
